// Make sure this is a type-only import, as importing it has side-effects
import type { datadogRum } from "@datadog/browser-rum";
import { defineGlobal } from "@faire/globals";

import { Level, LoggingHandler } from "../../logging/types";
import { LogMetadataKey } from "../../LogMetadataKey";

import { serializeError } from "./utils";

/**
 * Log handler for the browser. Handles both Datadog RUM and console logging for local environments.
 */
export const browserLoggingHandler: LoggingHandler = ({
  level,
  message,
  attributes,
  error,
  productArea,
  pod,
  pillar,
  oncall,
}) => {
  if (typeof window === "undefined") {
    throw new Error(
      "browserLoggingHandler should only be used in a browser environment."
    );
  }

  if (getEnvironment() === "local") {
    if (error?.argument) {
      attributes ??= {};
      attributes.error = error.instance;
    }
    // Emit the log to the console for easier debugging in local environments.
    const logFunction = console[consoleFunctions[level]].bind(console);

    if (!attributes) {
      logFunction(message);
    } else {
      logFunction(message, { ...attributes });
    }
  }

  const rum = getDatadogRum();
  if (!rum) {
    return;
  }

  attributes ??= {};
  attributes[LogMetadataKey.FAIRE_PRODUCT_AREA] ??= productArea;
  attributes[LogMetadataKey.FAIRE_POD] ??= pod;
  attributes[LogMetadataKey.FAIRE_PILLAR] ??= pillar;
  attributes[LogMetadataKey.FAIRE_ONCALL] ??= oncall;
  if (error) {
    attributes.error = serializeError(error.instance);
  }

  if (level === "info" || level === "debug") {
    rum.addAction?.(message, attributes);
  } else {
    rum.addError?.(error?.instance, {
      ...attributes,
      level,
      message,
    });
  }
};

export const { get: getDatadogRum } = defineGlobal<Partial<typeof datadogRum>>(
  "DD_RUM",
  { escapeFaireNamespace: true }
);
const { get: getEnvironment } = defineGlobal<string>("envName", {
  escapeFaireNamespace: true,
});

/**
 * @usedInTest
 */
export const consoleFunctions = {
  debug: "debug",
  info: "info",
  warn: "warn",
  error: "error",
  fatal: "error",
} satisfies Record<Level, keyof Console>;
